"use client";

import { Highlight, themes, Prism } from "prism-react-renderer";
import { useTheme } from "@/components/theme-provider";

/* Register extra languages using prism-react-renderer's Prism instance */
(typeof global !== "undefined" ? global : window).Prism = Prism;

/* Manually define language grammars compatible with prism-react-renderer */
Prism.languages.bash = {
  comment: { pattern: /#.*/, greedy: true },
  string: [
    { pattern: /\$'(?:[^'\\]|\\.)*'/, greedy: true },
    { pattern: /"(?:[^"\\$]|\\.|\$(?!\())*"/, greedy: true },
    { pattern: /'[^']*'/, greedy: true },
  ],
  variable: /\$[\w?#!@*-]+|\$\{[^}]+\}/,
  keyword: /\b(?:if|then|else|elif|fi|for|while|do|done|case|esac|function|return|in|select|until|local|export|declare|readonly|unset)\b/,
  builtin: /\b(?:echo|printf|read|cd|pwd|pushd|popd|dirs|source|exit|exec|alias|unalias|test|eval|set|shift|trap|wait|kill|true|false|command|type|hash|help|let|shopt)\b/,
  boolean: /\b(?:true|false)\b/,
  operator: /&&|\|\||[!=<>]=?|[|&;]+|\.\.|-[a-zA-Z]+/,
  function: /\b\w+(?=\s*\()/,
  punctuation: /[{}[\];(),.]/,
  number: /\b\d+(?:\.\d+)?\b/,
};

Prism.languages.java = Prism.languages.extend("clike", {
  keyword: /\b(?:abstract|assert|boolean|break|byte|case|catch|char|class|const|continue|default|do|double|else|enum|extends|final|finally|float|for|goto|if|implements|import|instanceof|int|interface|long|native|new|null|package|private|protected|public|return|short|static|strictfp|super|switch|synchronized|this|throw|throws|transient|try|var|void|volatile|while)\b/,
  string: { pattern: /(^|[^\\])"(?:\\.|[^"\\])*"/, lookbehind: true, greedy: true },
  number: /\b0b[01][01_]*L?\b|\b0x[\da-f_]*\.?[\da-f_p+-]+\b|(?:\b\d[\d_]*\.?[\d_]*|\B\.\d[\d_]*)(?:e[+-]?\d[\d_]*)?[dfl]?\b/i,
  annotation: { pattern: /@\w+/, alias: "builtin" },
});

Prism.languages.csharp = Prism.languages.extend("clike", {
  keyword: /\b(?:abstract|as|async|await|base|bool|break|byte|case|catch|char|checked|class|const|continue|decimal|default|delegate|do|double|else|enum|event|explicit|extern|false|finally|fixed|float|for|foreach|goto|if|implicit|in|int|interface|internal|is|lock|long|namespace|new|null|object|operator|out|override|params|private|protected|public|readonly|ref|return|sbyte|sealed|short|sizeof|stackalloc|static|string|struct|switch|this|throw|true|try|typeof|uint|ulong|unchecked|unsafe|ushort|using|var|virtual|void|volatile|while)\b/,
  string: [
    { pattern: /@"[^"]*(?:""[^"]*)*"/, greedy: true },
    { pattern: /(^|[^\\])"(?:\\.|[^"\\])*"/, lookbehind: true, greedy: true },
  ],
});

Prism.languages.dart = Prism.languages.extend("clike", {
  keyword: /\b(?:abstract|as|assert|async|await|break|case|catch|class|const|continue|covariant|default|deferred|do|dynamic|else|enum|export|extends|extension|external|factory|false|final|finally|for|Function|get|hide|if|implements|import|in|interface|is|late|library|mixin|new|null|on|operator|part|required|rethrow|return|set|show|static|super|switch|sync|this|throw|true|try|typedef|var|void|while|with|yield)\b/,
  string: [
    { pattern: /r"[^"]*"|r'[^']*'/, greedy: true },
    { pattern: /(^|[^\\])"(?:\\.|[^"\\])*"/, lookbehind: true, greedy: true },
    { pattern: /(^|[^\\])'(?:\\.|[^'\\])*'/, lookbehind: true, greedy: true },
  ],
});

Prism.languages.ruby = {
  comment: { pattern: /#.*/, greedy: true },
  string: [
    { pattern: /"(?:\\.|[^"\\])*"/, greedy: true },
    { pattern: /'(?:\\.|[^'\\])*'/, greedy: true },
  ],
  keyword: /\b(?:alias|and|begin|break|case|class|def|defined\?|do|else|elsif|end|ensure|false|for|if|in|module|next|nil|not|or|redo|require|rescue|retry|return|self|super|then|true|undef|unless|until|when|while|yield)\b/,
  symbol: /:\w+/,
  variable: /[@$]\w+/,
  boolean: /\b(?:true|false|nil)\b/,
  number: /\b\d+(?:\.\d+)?\b/,
  operator: /[+\-*/=<>!&|^~]+|\.{2,3}/,
  punctuation: /[{}[\];(),.:]/,
  function: /\b\w+[?!]?(?=\s*(?:\(|do|\{))/,
};

Prism.languages.php = Prism.languages.extend("clike", {
  keyword: /\b(?:abstract|and|array|as|break|callable|case|catch|class|clone|const|continue|declare|default|die|do|echo|else|elseif|empty|enddeclare|endfor|endforeach|endif|endswitch|endwhile|eval|exit|extends|final|finally|fn|for|foreach|function|global|goto|if|implements|include|include_once|instanceof|insteadof|interface|isset|list|match|namespace|new|or|print|private|protected|public|readonly|require|require_once|return|static|switch|throw|trait|try|unset|use|var|while|xor|yield)\b/i,
  variable: /\$\w+/,
  string: [
    { pattern: /<<<'([^']+)'[\r\n](?:.*[\r\n])*?\1;/, greedy: true },
    { pattern: /(^|[^\\])"(?:\\.|[^"\\])*"/, lookbehind: true, greedy: true },
    { pattern: /(^|[^\\])'(?:\\.|[^'\\])*'/, lookbehind: true, greedy: true },
  ],
});

Prism.languages.http = {
  "request-line": {
    pattern: /^(?:GET|HEAD|POST|PUT|DELETE|CONNECT|OPTIONS|TRACE|PATCH)\s.*$/m,
    inside: {
      method: /^[A-Z]+/,
      "request-target": { pattern: /\S+/, alias: "url" },
    },
  },
  "header-name": { pattern: /^[\w-]+(?=:)/m, alias: "keyword" },
  "header-value": { pattern: /(?<=:\s).+/m, alias: "string" },
};

interface CodeBlockProps {
  code: string;
  language: string;
  showLineNumbers?: boolean;
  forceDark?: boolean;
}

export function CodeBlock({ code, language, showLineNumbers = true, forceDark = false }: CodeBlockProps) {
  const { resolvedTheme } = useTheme();
  // Map our language names to Prism language keys
  const langMap: Record<string, string> = {
    curl: "bash",
    bash: "bash",
    node: "javascript",
    python: "python",
    go: "go",
    json: "json",
    java: "java",
    csharp: "csharp",
    dart: "dart",
    ruby: "ruby",
    php: "php",
    http: "http",
  };

  const prismLang = langMap[language] || language;
  const darkTheme = { ...themes.nightOwl, plain: { ...themes.nightOwl.plain, backgroundColor: "#0f1011" } };
  const codeTheme = forceDark || resolvedTheme !== "light" ? darkTheme : themes.oneLight;

  return (
    <Highlight theme={codeTheme} code={code.trim()} language={prismLang}>
      {({ tokens, getLineProps, getTokenProps }) => (
        <pre className="p-5 text-2xs font-mono leading-relaxed overflow-auto max-w-full whitespace-pre-wrap wrap-break-word" style={{ background: "transparent" }}>
          <code className="block min-w-0 w-full">
            {tokens.map((line, i) => {
              const lineProps = getLineProps({ line });
              return (
                <div key={i} {...lineProps} className="flex">
                  {showLineNumbers && (
                    <span className="select-none text-text-placeholder w-8 text-right mr-4 shrink-0">
                      {i + 1}
                    </span>
                  )}
                  <span className="min-w-0 flex-1 pl-[2ch] indent-[-2ch]">
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token })} />
                    ))}
                  </span>
                </div>
              );
            })}
          </code>
        </pre>
      )}
    </Highlight>
  );
}
